import { createRulerMarkup } from 'features/fde/rulers/rulerMarkup'
import { Ruler } from 'features/fde/rulers/rulerTypes'
import { RolodexEntryQueryBody } from 'features/rolodex'
import {
  GeckoComponent,
  GeckoComponentDataFrame,
  GeckoEntry,
  GeckoMarkupLayer,
  GeckoMeasurement,
  GeckoMeasurementRange,
  GeckoMeasurementUnit,
  RolodexEntryJson,
  RolodexEntryType,
} from 'types'

const DEFAULT_UNIT = GeckoMeasurementUnit.Millimeters

/**
 * The one RULER-by-indication query both ruler consumers issue
 * (`useFfsMarkupLayers` and `useLtaRulerDistances`). Kept in one place so their
 * SWR keys are structurally identical and dedupe to a single fetch, rather than
 * relying on two hand-written literals staying in sync. Returns `undefined` when
 * there are no indications, matching the "no query" sentinel both hooks use.
 */
export function rulerQueryForIndications(
  indicationIds: ReadonlyArray<string>
): RolodexEntryQueryBody | undefined {
  return indicationIds.length > 0
    ? {
        type: RolodexEntryType.RULER,
        tags: { [RolodexEntryType.INDICATION]: [...indicationIds] },
      }
    : undefined
}

interface RulerSelectionJson {
  x: {
    start: { num: number; unit: string }
    end: { num: number; unit: string }
  }
  y: {
    start: { num: number; unit: string }
    end: { num: number; unit: string }
  }
}

/**
 * Libra-local port of `features/fde/rulers/rulerApi.ts`'s private `entryToRuler`
 * + `selectionFromJson` (`rulerApi.ts:54-104`). Parses a Rolodex `RULER` entry
 * into a `Ruler` object that `createRulerMarkup` (the exported pure helper)
 * can consume.
 */
export function entryToRuler(entry: GeckoEntry): Ruler | null {
  const data = entry.data as {
    title?: string
    selection?: RulerSelectionJson
    wraps?: ('x' | 'y')[]
  }
  if (!data.selection) return null
  const sel = data.selection
  const toMeas = (n: number, unitId: string) =>
    GeckoMeasurement.CreateFromValue(
      n,
      GeckoMeasurementUnit.GetByID(unitId) ?? DEFAULT_UNIT
    )
  return {
    id: entry.id,
    title: data.title ?? '',
    selection: {
      x: GeckoMeasurementRange.CreateFromStartEnd(
        toMeas(sel.x.start.num, sel.x.start.unit),
        toMeas(sel.x.end.num, sel.x.end.unit)
      ),
      y: GeckoMeasurementRange.CreateFromStartEnd(
        toMeas(sel.y.start.num, sel.y.start.unit),
        toMeas(sel.y.end.num, sel.y.end.unit)
      ),
    },
    wraps: data.wraps,
  }
}

/**
 * Converts a single `RULER` Rolodex entry into a markup layer.
 *
 * `createRulerMarkup` requires a parsed `Ruler` + CDF geometry + selected
 * component + display unit. Skips if any are missing.
 */
export function rulerEntryToLayer(
  entry: GeckoEntry,
  cdf: GeckoComponentDataFrame | undefined,
  component: GeckoComponent | undefined,
  lengthUnit: GeckoMeasurementUnit
): GeckoMarkupLayer[] {
  if (!cdf || !component) return []
  const ruler = entryToRuler(entry)
  if (!ruler) return []
  const markup = createRulerMarkup(
    ruler,
    cdf.data.geometry,
    component,
    lengthUnit,
    { indication: entry.tags.indication }
  )
  if (!markup) return []
  // FFS-only: surface the ruler's name on the viz label. The shared
  // `createRulerMarkup` only puts the length in the `-r-label` primitive; the
  // title lives in `data.name`. Mutate the returned JSON (a fresh object per
  // call -- rulerMarkup.ts) rather than the shared builder so FDE's ruler
  // labels stay length-only. Titles are stored untrimmed, so trim to avoid a
  // "   · <len>" label from whitespace-only names.
  const title = ruler.title.trim()
  const primitives = (
    markup.data as {
      primitives?: Record<string, { kind?: string; text?: string }>
    }
  ).primitives
  if (title && primitives) {
    for (const primitive of Object.values(primitives)) {
      if (primitive.kind === 'label') {
        primitive.text = primitive.text ? `${title} · ${primitive.text}` : title
      }
    }
  }
  try {
    return [GeckoMarkupLayer.CreateFromJSON(markup as RolodexEntryJson)]
  } catch (err) {
    console.warn(`Failed to construct ruler markup layer ${entry.id}:`, err)
    return []
  }
}
